/** @file entryFor: chooses an impostor's facet entry by variant, frame key and pitch band (17.1-17.2, 18.4). */
import { PITCH_RANGES } from '@zilo/pose3d-format/tables/pitchRanges';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';

/**
 * The chosen entry (-1 = none), the frame key it serves (`frame`), the band it was matched with (`band`: the node's
 * band, or the nearest band of its ranges on a nearest-range fallback, -1 = none), whether the lookup folds
 * (`folded`: the entry has `yawFlip` and the node is yawFlipped) and `hidden` (`onMissingFrame: 'hide'` found nothing).
 */
export type EntryChoice = { entry: number; frame: number; band: number; folded: boolean; hidden: boolean };

/** Every variant the entry lists is active (an entry without variants never qualifies). */
function variantsOn(entry: FacetEntry, active: ReadonlySet<string>): boolean {
  if (entry.variants.length === 0) return false;
  for (const v of entry.variants) if (!active.has(v)) return false;
  return true;
}

/** The entry belongs to the pool: the variant entries when any is fully active, else the entries without variants. */
function inPool(entry: FacetEntry, variantPool: boolean, active: ReadonlySet<string>): boolean {
  return variantPool ? variantsOn(entry, active) : entry.variants.length === 0;
}

/** The pool entry serves `key`: it names the key, or it is generic while no pool entry names it. */
function servesKey(entry: FacetEntry, key: number, named: boolean): boolean {
  return named ? entry.frames.includes(key) : entry.frames.length === 0;
}

/** Width of the narrowest of the entry's ranges that contains band `b`; Infinity when none does. */
function narrowestWidth(entry: FacetEntry, b: number): number {
  let width = Number.POSITIVE_INFINITY;
  for (const name of entry.bands) {
    const [lo, hi] = PITCH_RANGES[name];
    if (b >= lo && b <= hi && hi - lo < width) width = hi - lo;
  }
  return width;
}

/** Distance in bands from `b` to the entry's nearest range (0 inside one). */
function nearestDistance(entry: FacetEntry, b: number): number {
  let dist = Number.POSITIVE_INFINITY;
  for (const name of entry.bands) {
    const [lo, hi] = PITCH_RANGES[name];
    dist = Math.min(dist, b < lo ? lo - b : b > hi ? b - hi : 0);
  }
  return dist;
}

/** The band of the entry's nearest range closest to `b` (first range on ties). */
function nearestBandOf(entry: FacetEntry, b: number): number {
  let dist = Number.POSITIVE_INFINITY;
  let at = b;
  for (const name of entry.bands) {
    const [lo, hi] = PITCH_RANGES[name];
    const c = Math.min(Math.max(b, lo), hi);
    if (Math.abs(c - b) < dist) { dist = Math.abs(c - b); at = c; }
  }
  return at;
}

/**
 * Entry choice (plan decision 13): pool = entries whose `variants` are all active (when any such entry exists), else
 * entries without variants; within it the entries naming `frameKey`, else the generic ones (`frames: []`); none ->
 * `onMissingFrame` `'default'` retries key 0 once, `'hide'` hides; still none -> `entry -1` (missing). Every entry
 * is matched with the unfolded `band` (vectors README, 18.4: the fold changes only a `yawFlip` entry's azimuth and
 * table, so `folded = entry.yawFlip && yawFlipped`); the entry whose narrowest containing `PITCH_RANGES` range is
 * narrowest wins (ties -> lower index); none contains the band -> the nearest range in bands (ties -> lower index).
 * Writes `out` and returns it; allocates nothing.
 */
export function entryFor(entries: readonly FacetEntry[], frameKey: number, band: number, yawFlipped: boolean,
  active: ReadonlySet<string>, onMissingFrame: 'default' | 'hide', out: EntryChoice): EntryChoice {
  let variantPool = false;
  for (const e of entries) if (variantsOn(e, active)) { variantPool = true; break; }
  let key = frameKey;
  let named = false;
  let any = false;
  for (let attempt = 0; attempt < 2 && !any; attempt++) {
    if (attempt === 1) {
      if (onMissingFrame === 'hide' || frameKey === 0 || entries.length === 0) break;
      key = 0;
    }
    named = false;
    for (const e of entries) if (inPool(e, variantPool, active) && e.frames.includes(key)) { named = true; break; }
    for (const e of entries) if (inPool(e, variantPool, active) && servesKey(e, key, named)) { any = true; break; }
  }
  out.folded = false;
  if (!any) {
    const hide = onMissingFrame === 'hide' && entries.length > 0;
    out.entry = -1; out.frame = frameKey; out.band = -1; out.hidden = hide;
    return out;
  }
  let best = -1;
  let bestWidth = Number.POSITIVE_INFINITY;
  let nearest = -1;
  let nearestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i] as FacetEntry;
    if (!inPool(e, variantPool, active) || !servesKey(e, key, named)) continue;
    const width = narrowestWidth(e, band);
    if (width < bestWidth) { best = i; bestWidth = width; }
    const dist = nearestDistance(e, band);
    if (dist < nearestDist) { nearest = i; nearestDist = dist; }
  }
  const entry = best >= 0 ? best : nearest;
  out.entry = entry;
  out.frame = key;
  const chosen = entries[entry] as FacetEntry;
  out.band = best >= 0 ? band : nearestBandOf(chosen, band);
  out.folded = chosen.yawFlip === true && yawFlipped;
  out.hidden = false;
  return out;
}
